import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import StudentHeader from '@/components/student/StudentHeader';

import EducationForm from '@/components/student/EducationForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { User, Save, AlertCircle, CheckCircle, Camera, GraduationCap, ChevronDown, ChevronUp, PenLine, TrendingUp, Award, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentProfilePage = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    photo_url: '',
    father_name: '',
    mother_name: '',
    dob: '',
    gender: '',
    category: '',
    address: '',
    city: '',
    district: '',
    state: '',
    pin_code: '',
    qualification: ''
  });
  const [isComplete, setIsComplete] = useState(false);
  
  // Education states
  const [educationData, setEducationData] = useState(null);
  const [showEducationForm, setShowEducationForm] = useState(false);
  const [educationExpanded, setEducationExpanded] = useState(true);

  // Profile completion calculation
  const profileCompletion = useMemo(() => {
    const fields = [
      { key: 'photo_url', label: 'Profile Photo', weight: 5, filled: !!profile.photo_url },
      { key: 'father_name', label: "Father's Name", weight: 10, filled: !!profile.father_name },
      { key: 'mother_name', label: "Mother's Name", weight: 10, filled: !!profile.mother_name },
      { key: 'dob', label: 'Date of Birth', weight: 10, filled: !!profile.dob },
      { key: 'gender', label: 'Gender', weight: 5, filled: !!profile.gender },
      { key: 'category', label: 'Category', weight: 5, filled: !!profile.category },
      { key: 'qualification', label: 'Qualification', weight: 10, filled: !!profile.qualification },
      { key: 'address', label: 'Address', weight: 10, filled: !!profile.address },
      { key: 'city', label: 'City', weight: 5, filled: !!profile.city },
      { key: 'district', label: 'District', weight: 5, filled: !!profile.district },
      { key: 'state', label: 'State', weight: 5, filled: !!profile.state },
      { key: 'pin_code', label: 'PIN Code', weight: 5, filled: !!profile.pin_code && profile.pin_code.length === 6 },
      { key: 'education', label: 'Education Details', weight: 15, filled: !!educationData?.highest_qualification }
    ];

    const totalWeight = fields.reduce((sum, f) => sum + f.weight, 0);
    const filledWeight = fields.reduce((sum, f) => sum + (f.filled ? f.weight : 0), 0);
    const percentage = Math.round((filledWeight / totalWeight) * 100);
    const missing = fields.filter(f => !f.filled).map(f => f.label);

    return { percentage, missing, fields };
  }, [profile, educationData]);

  const qualifications = [
    '5th Pass',
    '8th Pass',
    '10th Pass',
    '12th Pass',
    'ITI',
    'Diploma',
    'Graduate',
    'Post Graduate',
    'Other'
  ];

  const genderOptions = [
    'Male',
    'Female',
    'Other'
  ];

  const categoryOptions = [
    'General',
    'OBC',
    'SC',
    'ST',
    'EWS',
    'Other'
  ];

  const states = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
  ];

  // Districts state for auto-dropdown
  const [districts, setDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // Fetch districts when state changes
  useEffect(() => {
    if (profile.state) {
      fetchDistricts(profile.state);
    } else {
      setDistricts([]);
    }
  }, [profile.state]);

  const fetchDistricts = async (state) => {
    setLoadingDistricts(true);
    try {
      const response = await axios.get(`${API}/districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
    setLoadingDistricts(false);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth/login');
      return;
    }
    fetchProfile();
    fetchEducation();
  }, [isAuthenticated, navigate]);

  const fetchEducation = async () => {
    try {
      const token = localStorage.getItem('token');
      let edu = null;
      try {
        const res = await axios.get(`${API}/student/education`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        edu = res.data;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('education endpoint:', e?.response?.status);
      }

      // If the student-education table is empty/unset, fall back to center-supplied
      // data from /profile/student so the Qualification dropdown auto-populates.
      if (!edu || !edu.highest_qualification) {
        try {
          const profRes = await axios.get(`${API}/profile/student`);
          const p = profRes.data || {};
          const mapped = normalizeQualification(p.education_level || p.qualification);
          if (mapped) {
            // Center stores nested education as { board, year, result } — map those
            // to the field names this Profile page expects.
            const mapDetail = (level, src) => {
              if (!src || typeof src !== 'object') return null;
              const yr = parseInt(src.year || src.passing_year, 10);
              const pct = parseFloat(src.percentage || src.result || src.result_grade);
              const totalMarks = src.total_marks ?? '';
              const obtainedMarks = src.obtained_marks ?? '';
              return {
                level,
                board_or_university: src.board_or_university || src.board || src.university || '',
                passing_year: Number.isFinite(yr) ? yr : '',
                percentage: Number.isFinite(pct) ? pct : '',
                total_marks: totalMarks,
                obtained_marks: obtainedMarks,
                stream: src.stream ?? '',
                course_name: src.course_name || src.degree || '',
                specialization: src.specialization ?? '',
              };
            };
            const details = [];
            const d10 = mapDetail('10th', p.education_10th); if (d10) details.push(d10);
            const d12 = mapDetail('12th', p.education_12th); if (d12) details.push(d12);
            const di  = mapDetail('iti',  p.education_iti);  if (di)  details.push(di);
            const dg  = mapDetail('graduation', p.education_graduation); if (dg) details.push(dg);
            const dpg = mapDetail('post_graduation', p.education_pg);    if (dpg) details.push(dpg);
            edu = {
              highest_qualification: mapped,
              education_details: details,
            };
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.warn('profile fallback:', e?.response?.status);
        }
      }

      setEducationData(edu);
    } catch (error) {
      console.error('Failed to fetch education:', error);
      setEducationData(null);
    }
  };

  // Map backend education_level → dropdown value (lowercase keys).
  const normalizeQualification = (raw) => {
    if (!raw) return '';
    const s = String(raw).toLowerCase().trim();
    if (s.includes('post') && s.includes('grad')) return 'post_graduation';
    if (s.includes('graduat')) return 'graduation';
    if (s.includes('12')) return '12th';
    if (s.includes('10')) return '10th';
    // Other values (5th/8th/iti/diploma) don't have dropdown options yet → ignore.
    return '';
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/student`);
      const data = response.data || {};
      // Backend field names → frontend field names (single normalisation point).
      const normalised = {
        ...data,
        photo_url: data.photo_url || data.photo || '',
        qualification: data.qualification || data.education_level || '',
      };
      setProfile(prev => ({ ...prev, ...normalised }));
      setIsComplete(data.is_complete || false);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'state') {
      // Clear district when state changes
      setProfile(prev => ({ ...prev, [field]: value, district: '' }));
    } else {
      setProfile(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate ALL required fields
    const required = [
      { field: 'father_name', label: "Father's Name" },
      { field: 'mother_name', label: "Mother's Name" },
      { field: 'dob', label: 'Date of Birth' },
      { field: 'address', label: 'Address' },
      { field: 'city', label: 'City' },
      { field: 'district', label: 'District' },
      { field: 'state', label: 'State' },
      { field: 'pin_code', label: 'PIN Code' }
    ];
    
    const missing = required.filter(r => !profile[r.field]);
    
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.map(m => m.label).join(', ')}`);
      return;
    }

    if (profile.pin_code.length !== 6) {
      toast.error('Please enter a valid 6-digit PIN code');
      return;
    }

    // Validate education
    if (!educationData?.highest_qualification) {
      toast.error('Please select your qualification');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save profile
      const profileResponse = await axios.put(`${API}/profile/student`, {
        ...profile,
        qualification: educationData.highest_qualification // Sync qualification
      });
      
      // Save education
      await axios.put(`${API}/student/education`, {
        highest_qualification: educationData.highest_qualification,
        education_details: educationData.education_details || []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsComplete(profileResponse.data.is_complete);
      toast.success('Profile and Education saved successfully!');
      
      navigate('/student/dashboard');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <StudentHeader />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
        
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <StudentHeader />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Profile Completion Card */}
        <div className={`rounded-2xl p-6 mb-6 relative overflow-hidden ${
          profileCompletion.percentage === 100 
            ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
            : profileCompletion.percentage >= 70 
              ? 'bg-gradient-to-r from-blue-500 to-indigo-500'
              : 'bg-gradient-to-r from-orange-500 to-amber-500'
        }`} data-testid="profile-completion-card">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {profileCompletion.percentage === 100 ? (
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h2 className="text-lg font-bold text-white">Profile Completion</h2>
                  <p className="text-white/80 text-sm">
                    {profileCompletion.percentage === 100 
                      ? 'Your profile is complete!' 
                      : `${profileCompletion.missing.length} field${profileCompletion.missing.length > 1 ? 's' : ''} remaining`}
                  </p>
                </div>
              </div>
              
              {/* Circular Progress */}
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="6"
                    fill="none"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    stroke="white"
                    strokeWidth="6"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 35}`}
                    strokeDashoffset={`${2 * Math.PI * 35 * (1 - profileCompletion.percentage / 100)}`}
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-white">{profileCompletion.percentage}%</span>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden mb-3">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${profileCompletion.percentage}%` }}
              ></div>
            </div>

            {/* Missing Fields */}
            {profileCompletion.missing.length > 0 && profileCompletion.missing.length <= 5 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {profileCompletion.missing.map((field, index) => (
                  <span 
                    key={index}
                    className="px-2 py-1 bg-white/20 rounded-full text-xs text-white font-medium"
                  >
                    {field}
                  </span>
                ))}
              </div>
            )}

            {profileCompletion.percentage === 100 && (
              <div className="flex items-center gap-2 mt-2 text-white/90">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm">You can now enroll in courses and take assessments!</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Student Profile</h1>
              <p className="text-slate-600">{user?.name} • {user?.phone}</p>
              {profile.enrollment_number && (
                <p className="text-sm font-mono font-semibold text-blue-700 mt-1" data-testid="enrollment-number">
                  Enrollment No: {profile.enrollment_number}
                  {profile.center_code && <span className="text-slate-500 ml-2">({profile.center_code})</span>}
                </p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Photo Upload */}
            <div className="border-b border-slate-200 pb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Profile Photo (प्रोफाइल फोटो)</h2>
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-28 h-28 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                    {profile.photo_url ? (
                      <img src={profile.photo_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="h-10 w-10 text-slate-400" />
                    )}
                  </div>
                  {profile.photo_url && (
                    <button
                      type="button"
                      onClick={() => handleChange('photo_url', '')}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          // Check file type
                          if (!['image/jpeg', 'image/png'].includes(file.type)) {
                            toast.error('Only JPG and PNG formats are allowed');
                            return;
                          }
                          
                          // Check file size (20KB = 20 * 1024 bytes)
                          if (file.size > 20 * 1024) {
                            toast.error('Photo size must be less than 20 KB');
                            return;
                          }
                          
                          // Convert to base64
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            handleChange('photo_url', event.target.result);
                            toast.success('Photo uploaded successfully!');
                          };
                          reader.onerror = () => {
                            toast.error('Failed to read photo');
                          };
                          reader.readAsDataURL(file);
                        }}
                        data-testid="photo-upload-input"
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                        <Camera className="w-4 h-4" />
                        Upload Photo
                      </span>
                    </label>
                    {profile.photo_url && (
                      <span className="text-sm text-emerald-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Uploaded
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Passport size photo • JPG or PNG • Max 20 KB
                  </p>
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div className="border-b border-slate-200 pb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Personal Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="father_name">Father's Name (पिता का नाम) *</Label>
                  <Input
                    id="father_name"
                    value={profile.father_name || ''}
                    onChange={(e) => handleChange('father_name', e.target.value)}
                    placeholder="Enter father's name"
                    required
                    className="mt-1"
                    data-testid="father-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="mother_name">Mother's Name (माता का नाम) *</Label>
                  <Input
                    id="mother_name"
                    value={profile.mother_name || ''}
                    onChange={(e) => handleChange('mother_name', e.target.value)}
                    placeholder="Enter mother's name"
                    required
                    className="mt-1"
                    data-testid="mother-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="dob">Date of Birth (जन्म तिथि) *</Label>
                  <Input
                    id="dob"
                    type="date"
                    value={profile.dob || ''}
                    onChange={(e) => handleChange('dob', e.target.value)}
                    required
                    className="mt-1"
                    data-testid="dob-input"
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gender (लिंग) *</Label>
                  <Select value={profile.gender || ''} onValueChange={(v) => handleChange('gender', v)}>
                    <SelectTrigger className="mt-1" data-testid="gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      {genderOptions.map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="category">Category (श्रेणी) *</Label>
                  <Select value={profile.category || ''} onValueChange={(v) => handleChange('category', v)}>
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Address (पता)</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="address">Complete Address (पूरा पता) *</Label>
                  <Input
                    id="address"
                    value={profile.address || ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="House No., Street, Area"
                    required
                    className="mt-1"
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City (शहर) *</Label>
                    <Input
                      id="city"
                      value={profile.city || ''}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="Enter city"
                      required
                      className="mt-1"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State (राज्य) *</Label>
                    <Select value={profile.state || ''} onValueChange={(v) => handleChange('state', v)}>
                      <SelectTrigger className="mt-1" data-testid="state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="district">District (जिला) *</Label>
                    <Select 
                      value={profile.district || ''} 
                      onValueChange={(v) => handleChange('district', v)}
                      disabled={!profile.state || loadingDistricts}
                    >
                      <SelectTrigger className="mt-1" data-testid="district-select">
                        <SelectValue placeholder={loadingDistricts ? 'Loading...' : 'Select district'} />
                      </SelectTrigger>
                      <SelectContent>
                        {districts.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="pin_code">PIN Code (पिन कोड) *</Label>
                    <Input
                      id="pin_code"
                      value={profile.pin_code || ''}
                      onChange={(e) => handleChange('pin_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit PIN"
                      required
                      maxLength={6}
                      className="mt-1"
                      data-testid="pincode-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Education Section - Inline */}
            <div className="border-t pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Education Details (शैक्षिक विवरण)</h2>
                  <p className="text-sm text-slate-500">Required for course eligibility check</p>
                </div>
              </div>

              {/* Qualification Dropdown */}
              <div className="mb-4">
                <Label htmlFor="highest_qualification">Qualification (योग्यता) *</Label>
                <Select 
                  value={educationData?.highest_qualification || ''} 
                  onValueChange={(v) => {
                    setEducationData(prev => ({ ...prev, highest_qualification: v, education_details: prev?.education_details || [] }));
                    setShowEducationForm(true);
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="education-qualification-select">
                    <SelectValue placeholder="-- Select Your Highest Qualification --" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10th">10th Pass (High School)</SelectItem>
                    <SelectItem value="12th">12th Pass (Intermediate)</SelectItem>
                    <SelectItem value="graduation">Graduation</SelectItem>
                    <SelectItem value="post_graduation">Post Graduation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Education Details Form - Shows based on selected qualification */}
              {educationData?.highest_qualification && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-4">
                  <h3 className="font-medium text-slate-800">Fill Your Education Details (शैक्षिक विवरण भरें)</h3>
                  
                  {/* 10th Details - Always show if any qualification selected */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      10th (High School)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Board/University</Label>
                        <Input
                          placeholder="CBSE/RBSE"
                          className="h-9 text-sm"
                          value={educationData.education_details?.find(e => e.level === '10th')?.board_or_university || ''}
                          onChange={(e) => {
                            const existing = educationData.education_details?.filter(ed => ed.level !== '10th') || [];
                            const current = educationData.education_details?.find(ed => ed.level === '10th') || { level: '10th' };
                            setEducationData({
                              ...educationData,
                              education_details: [...existing, { ...current, board_or_university: e.target.value }]
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Passing Year</Label>
                        <Input
                          type="number"
                          placeholder="2020"
                          className="h-9 text-sm"
                          value={educationData.education_details?.find(e => e.level === '10th')?.passing_year || ''}
                          onChange={(e) => {
                            const existing = educationData.education_details?.filter(ed => ed.level !== '10th') || [];
                            const current = educationData.education_details?.find(ed => ed.level === '10th') || { level: '10th' };
                            setEducationData({
                              ...educationData,
                              education_details: [...existing, { ...current, passing_year: parseInt(e.target.value) || '' }]
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Percentage (%)</Label>
                        <Input
                          type="number"
                          placeholder="75"
                          className="h-9 text-sm"
                          value={educationData.education_details?.find(e => e.level === '10th')?.percentage || ''}
                          onChange={(e) => {
                            const existing = educationData.education_details?.filter(ed => ed.level !== '10th') || [];
                            const current = educationData.education_details?.find(ed => ed.level === '10th') || { level: '10th' };
                            setEducationData({
                              ...educationData,
                              education_details: [...existing, { ...current, percentage: parseFloat(e.target.value) || '' }]
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Total Marks</Label>
                        <Input
                          type="number"
                          placeholder="500"
                          className="h-9 text-sm"
                          value={educationData.education_details?.find(e => e.level === '10th')?.total_marks || ''}
                          onChange={(e) => {
                            const existing = educationData.education_details?.filter(ed => ed.level !== '10th') || [];
                            const current = educationData.education_details?.find(ed => ed.level === '10th') || { level: '10th' };
                            setEducationData({
                              ...educationData,
                              education_details: [...existing, { ...current, total_marks: parseInt(e.target.value) || '' }]
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* 12th Details - Show if 12th or higher */}
                  {['12th', 'graduation', 'post_graduation'].includes(educationData.highest_qualification) && (
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        12th (Intermediate)
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Board/University</Label>
                          <Input
                            placeholder="CBSE/RBSE"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === '12th')?.board_or_university || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== '12th') || [];
                              const current = educationData.education_details?.find(ed => ed.level === '12th') || { level: '12th' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, board_or_university: e.target.value }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Passing Year</Label>
                          <Input
                            type="number"
                            placeholder="2022"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === '12th')?.passing_year || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== '12th') || [];
                              const current = educationData.education_details?.find(ed => ed.level === '12th') || { level: '12th' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, passing_year: parseInt(e.target.value) || '' }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Percentage (%)</Label>
                          <Input
                            type="number"
                            placeholder="80"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === '12th')?.percentage || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== '12th') || [];
                              const current = educationData.education_details?.find(ed => ed.level === '12th') || { level: '12th' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, percentage: parseFloat(e.target.value) || '' }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Total Marks</Label>
                          <Input
                            type="number"
                            placeholder="500"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === '12th')?.total_marks || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== '12th') || [];
                              const current = educationData.education_details?.find(ed => ed.level === '12th') || { level: '12th' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, total_marks: parseInt(e.target.value) || '' }]
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Graduation Details - Show if graduation or higher */}
                  {['graduation', 'post_graduation'].includes(educationData.highest_qualification) && (
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Graduation
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">University</Label>
                          <Input
                            placeholder="University name"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === 'graduation')?.board_or_university || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== 'graduation') || [];
                              const current = educationData.education_details?.find(ed => ed.level === 'graduation') || { level: 'graduation' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, board_or_university: e.target.value }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Passing Year</Label>
                          <Input
                            type="number"
                            placeholder="2024"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === 'graduation')?.passing_year || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== 'graduation') || [];
                              const current = educationData.education_details?.find(ed => ed.level === 'graduation') || { level: 'graduation' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, passing_year: parseInt(e.target.value) || '' }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Percentage (%)</Label>
                          <Input
                            type="number"
                            placeholder="70"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === 'graduation')?.percentage || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== 'graduation') || [];
                              const current = educationData.education_details?.find(ed => ed.level === 'graduation') || { level: 'graduation' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, percentage: parseFloat(e.target.value) || '' }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Degree</Label>
                          <Input
                            placeholder="B.A/B.Sc/BCA"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === 'graduation')?.degree || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== 'graduation') || [];
                              const current = educationData.education_details?.find(ed => ed.level === 'graduation') || { level: 'graduation' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, degree: e.target.value }]
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Post Graduation Details - Show only if post_graduation */}
                  {educationData.highest_qualification === 'post_graduation' && (
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Post Graduation
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">University</Label>
                          <Input
                            placeholder="University name"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === 'post_graduation')?.board_or_university || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== 'post_graduation') || [];
                              const current = educationData.education_details?.find(ed => ed.level === 'post_graduation') || { level: 'post_graduation' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, board_or_university: e.target.value }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Passing Year</Label>
                          <Input
                            type="number"
                            placeholder="2026"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === 'post_graduation')?.passing_year || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== 'post_graduation') || [];
                              const current = educationData.education_details?.find(ed => ed.level === 'post_graduation') || { level: 'post_graduation' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, passing_year: parseInt(e.target.value) || '' }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Percentage (%)</Label>
                          <Input
                            type="number"
                            placeholder="65"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === 'post_graduation')?.percentage || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== 'post_graduation') || [];
                              const current = educationData.education_details?.find(ed => ed.level === 'post_graduation') || { level: 'post_graduation' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, percentage: parseFloat(e.target.value) || '' }]
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Degree</Label>
                          <Input
                            placeholder="M.A/M.Sc/MCA"
                            className="h-9 text-sm"
                            value={educationData.education_details?.find(e => e.level === 'post_graduation')?.degree || ''}
                            onChange={(e) => {
                              const existing = educationData.education_details?.filter(ed => ed.level !== 'post_graduation') || [];
                              const current = educationData.education_details?.find(ed => ed.level === 'post_graduation') || { level: 'post_graduation' };
                              setEducationData({
                                ...educationData,
                                education_details: [...existing, { ...current, degree: e.target.value }]
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit - After Education */}
            <div className="flex justify-end gap-4 pt-6 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => navigate('/student/dashboard')}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">
                {saving ? 'Saving...' : (
                  <>
                    <Save className="h-4 w-4 mr-2" /> Save Profile
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      
    </div>
  );
};

export default StudentProfilePage;
